import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * useUrlFilters — two-way binding between a filters object and URL searchParams.
 *
 * Each key in `defaults` maps to a single URL parameter. Empty values
 * (`""`, `null`, `undefined`, `false`) are stripped from the URL so it stays
 * clean and shareable. Updates are written via `replace` so back/forward only
 * crosses meaningful navigation events.
 *
 * Usage:
 *   const [filters, setFilters, resetFilters, activeCount] = useUrlFilters({
 *     stage: "",
 *     source: "",
 *     date_from: "",
 *   });
 *   setFilters(prev => ({ ...prev, stage: "qualified" }));
 *
 * The optional `parsers` map lets you customise per-key serialization
 * (useful for booleans, comma-separated arrays, etc).
 */
const defaultParse = (raw, fallback) => {
  if (raw === null || raw === undefined) return fallback;
  if (typeof fallback === "boolean") return raw === "true";
  if (typeof fallback === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  }
  if (Array.isArray(fallback)) return raw.split(",").filter(Boolean);
  return raw;
};

const defaultSerialize = (value) => {
  if (value === null || value === undefined || value === "" || value === false) return null;
  if (Array.isArray(value)) return value.length ? value.join(",") : null;
  return String(value);
};

export function useUrlFilters(defaults, options = {}) {
  const { parsers = {}, serializers = {}, preserveKeys = [] } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultsRef = useRef(defaults);

  // Build current filters object by reading each key from URL or falling back to defaults
  const filters = useMemo(() => {
    const out = { ...defaultsRef.current };
    Object.keys(defaultsRef.current).forEach((key) => {
      const raw = searchParams.get(key);
      const fallback = defaultsRef.current[key];
      out[key] = (parsers[key] || defaultParse)(raw, fallback);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const writeToUrl = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          // Preserve any non-managed keys (e.g. ?tab=, ?article=)
          const managed = new Set([...Object.keys(defaultsRef.current), ...preserveKeys]);
          // Remove all managed keys then re-add non-empty values
          Object.keys(defaultsRef.current).forEach((k) => params.delete(k));
          Object.entries(next).forEach(([k, v]) => {
            const ser = (serializers[k] || defaultSerialize)(v);
            if (ser !== null) params.set(k, ser);
          });
          // Drop unrelated keys we don't recognise neither managed nor preserved? leave them
          for (const key of Array.from(params.keys())) {
            if (!managed.has(key) && !preserveKeys.includes(key)) {
              // keep — caller may have additional URL state we don't manage
            }
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams, parsers, serializers, preserveKeys]
  );

  const setFilters = useCallback(
    (updater) => {
      const next = typeof updater === "function" ? updater(filters) : { ...filters, ...updater };
      writeToUrl(next);
    },
    [filters, writeToUrl]
  );

  const resetFilters = useCallback(() => {
    writeToUrl(defaultsRef.current);
  }, [writeToUrl]);

  const activeCount = useMemo(
    () =>
      Object.entries(filters).reduce((acc, [k, v]) => {
        const def = defaultsRef.current[k];
        const equal =
          v === def || (Array.isArray(v) && Array.isArray(def) && v.length === 0 && def.length === 0);
        return equal ? acc : acc + 1;
      }, 0),
    [filters]
  );

  return [filters, setFilters, resetFilters, activeCount];
}

export default useUrlFilters;
